// ---------------------------------------------------------------------------
// The `laFolletteBanner` theme — a SECOND La Follette High School theme styled
// after the school's classic letterhead: a slim maroon HEADER band that carries
// the fixed school name "La Follette High School" (a text DECORATION, the same on
// every slide — NOT the slide's own title), a slim maroon FOOTER band with the
// Lancer "L" emblem straddling it at the bottom-right, and the slide's real title
// + content living in the white body in between.
//
// Authored DRY as deviations from `default`. Shares the brand palette with the
// sibling `laFollette` theme (colors sampled from the official "L" logo).
//
// SCHOOL NAME IS CHROME, NOT CONTENT: the header/footer bands AND the school-name
// text are decoration (z0, render-only, not selectable), so they read identically
// across the deck and never collide with the editable title/body. Each template
// places its own TITLE placeholder in the body.
//
// IMAGE DECORATION: the footer emblem uses the school's public CDN URL — the same
// deliberate external dependency documented in `la-follette.ts` (must stay
// reachable for the editor preview and the Google Slides exporter's `createImage`).
// ---------------------------------------------------------------------------

import type { ThemeDefinition } from '../registry';

/** Official Lancer "L" logo (school CDN). Public + stable (versioned path); the
 *  Google Slides exporter fetches this URL at export time, so it must stay
 *  publicly reachable. */
const LANCER_LOGO_URL =
  'https://resources.finalsite.net/images/f_auto,q_auto,t_image_size_2/v1759427364/madisonk12wius/yreivyjoponplxstabje/la-follette-logo.png';

/** Maroon header/footer bands, each with a slim detached maroon rule, the fixed
 *  white school-name in the header band, and the Lancer "L" emblem on the footer.
 *  Shared by every non-blank template so the letterhead reads identically. */
const CHROME = [
  // Slim maroon header band + detached maroon underline rule below it.
  {
    type: 'shape' as const,
    position: { x: 0, y: 0, width: 960, height: 35, rotation: 0, zIndex: 0 },
    content: { shapeType: 'rectangle' as const, fillColor: '$primary', strokeWidth: 0 },
  },
  {
    type: 'shape' as const,
    position: { x: 0, y: 40, width: 960, height: 6, rotation: 0, zIndex: 0 },
    content: { shapeType: 'rectangle' as const, fillColor: '$primary', strokeWidth: 0 },
  },
  // Fixed school name, centered white in the header band (drawn after the band).
  {
    type: 'text' as const,
    position: { x: 0, y: 0, width: 960, height: 45, rotation: 0, zIndex: 0 },
    content: { text: 'La Follette High School' },
    style: {
      fontFamily: 'Trebuchet MS, Verdana, sans-serif',
      color: '$primaryContrast',
      fontWeight: 'bold',
      fontSize: 19,
      textAlign: 'center' as const,
    },
  },
  // Slim maroon footer band + detached maroon overline rule above it (mirrors top).
  {
    type: 'shape' as const,
    position: { x: 0, y: 500, width: 960, height: 6, rotation: 0, zIndex: 0 },
    content: { shapeType: 'rectangle' as const, fillColor: '$primary', strokeWidth: 0 },
  },
  {
    type: 'shape' as const,
    position: { x: 0, y: 510, width: 960, height: 30, rotation: 0, zIndex: 0 },
    content: { shapeType: 'rectangle' as const, fillColor: '$primary', strokeWidth: 0 },
  },
  // Lancer "L" emblem straddling the footer band at the bottom-right.
  {
    type: 'image' as const,
    position: { x: 812, y: 408, width: 128, height: 128, rotation: 0, zIndex: 0 },
    content: {
      src: LANCER_LOGO_URL,
      alt: 'La Follette High School Lancers logo',
      objectFit: 'contain' as const,
    },
  },
];

/** The slide's own maroon title, top-left in the white body. Identical on the two
 *  content templates (the TITLE slide uses a larger centered hero instead). */
const CONTENT_TITLE = {
  placeholderId: 'TITLE' as const,
  position: { x: 60, y: 64, width: 840, height: 56 },
  textStyle: { fontSize: 30, color: '$primary', textAlign: 'left' as const, lineHeight: 1.05 },
};

export const LA_FOLLETTE_BANNER_THEME: ThemeDefinition = {
  themeId: 'laFolletteBanner',
  name: 'La Follette Banner',
  baseThemeId: 'default',
  palette: {
    background: '#FCFCFC', // crisp near-white body (the letterhead "page")
    surface: '#F0EEEE', // faint warm panel
    text: '#26211F', // near-black, faint warm cast
    textMuted: '#6E6663', // warm gray
    primary: '#9E2032', // La Follette maroon (logo fill)
    primaryContrast: '#FFFFFF',
    secondary: '#3A3A3D', // graphite — subtitle / supporting text
    secondaryContrast: '#FFFFFF',
    accent: '#9C9FA1', // La Follette silver (reserved; brand color)
  },
  fonts: {
    headingFont: "'Arial Black', Arial, sans-serif", // heavy varsity banner weight
    bodyFont: 'Helvetica, Arial, sans-serif',
  },
  layouts: {
    // Title slide: the slide's own title is a large centered maroon hero in the
    // body, with an optional subtitle; the letterhead chrome frames it.
    TITLE: {
      placeholders: [
        {
          placeholderId: 'TITLE',
          position: { x: 80, y: 200, width: 800, height: 112 },
          textStyle: { fontSize: 44, color: '$primary', textAlign: 'center', lineHeight: 1.05 },
        },
        {
          placeholderId: 'SUBTITLE',
          position: { x: 80, y: 324, width: 800, height: 60 },
          textStyle: { fontSize: 24, color: '$secondary', textAlign: 'center', lineHeight: 1.2 },
        },
      ],
      decoration: CHROME,
    },
    // Content slide: own title top-left in the body, body fills the page below it.
    TITLE_AND_BODY: {
      placeholders: [
        CONTENT_TITLE,
        {
          placeholderId: 'BODY',
          position: { x: 60, y: 132, width: 840, height: 348 },
          textStyle: { color: '$text' },
        },
      ],
      decoration: CHROME,
    },
    // Two columns: own title top-left; a thin maroon divider splits the body.
    TITLE_AND_TWO_COLUMNS: {
      placeholders: [
        CONTENT_TITLE,
        {
          placeholderId: 'LEFT_BODY',
          position: { x: 60, y: 132, width: 400, height: 348 },
          textStyle: { color: '$text' },
        },
        {
          placeholderId: 'RIGHT_BODY',
          position: { x: 500, y: 132, width: 400, height: 348 },
          textStyle: { color: '$text' },
        },
      ],
      decoration: [
        ...CHROME,
        {
          type: 'shape',
          position: { x: 478, y: 140, width: 3, height: 332, rotation: 0, zIndex: 0 },
          content: { shapeType: 'rectangle', fillColor: '$primary', strokeWidth: 0 },
        },
      ],
    },
    // BLANK inherits the base's empty decoration + the light page background.
  },
};
